import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import BottomNav from '@/components/BottomNav';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Heart, MapPin, Briefcase, Phone, ExternalLink, Sun, Moon, User, CheckCircle, X, ChevronDown } from 'lucide-react';
import { motion, useDragControls } from 'framer-motion';
import { MARRIAGE_STATUS_OPTIONS } from '@/data/profileFields';
import { ReportButton } from '@/components/ReportDialog';
import axios from 'axios';

export default function LikedPage() {
  const { token, API } = useAuth();
  const { lang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);

  // Drag controls for swipe-down-to-close on profile detail modal.
  const dragControls = useDragControls();
  const closeProfileModal = () => { setSelectedProfile(null); setPhotoIdx(0); };

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchLiked = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/members/liked`, { headers });
      setProfiles(res.data.profiles || []);
    } catch (err) { console.error('[LikedPage] fetchLiked failed:', err); }
    setLoading(false);
  }, [API, headers]);

  useEffect(() => { fetchLiked(); }, [fetchLiked]);

  const getPhoto = (p) => p?.photos?.find(ph => ph) || '';
  const getMarriageLabel = (val) => {
    const opt = MARRIAGE_STATUS_OPTIONS.find(o => o.value === val);
    return opt ? (lang === 'en' ? opt.en : opt.id) : val;
  };

  return (
    <div className="min-h-screen t-bg pb-24">
      <div className="sticky top-0 z-40 glass px-4 py-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#D98A94]" fill="#D98A94" />
            <h1 className="text-lg font-bold t-text">{lang === 'en' ? 'Liked Profiles' : 'Profil Disukai'}</h1>
          </div>
          <button onClick={toggleTheme} data-testid="liked-theme-toggle" className="p-2 rounded-full glass t-text-secondary">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-4 pb-2">
        <p className="t-text-muted text-xs">{profiles.length} {lang === 'en' ? 'profiles you liked' : 'profil yang Anda sukai'}</p>
      </div>

      <div className="max-w-md mx-auto px-4">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#D98A94] border-t-transparent rounded-full animate-spin" /></div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 mx-auto mb-6 t-text-muted" />
            <p className="t-text-secondary text-lg mb-2">{lang === 'en' ? 'No liked profiles yet' : 'Belum ada profil yang disukai'}</p>
            <p className="t-text-muted text-sm">{lang === 'en' ? 'Browse members and tap the heart to like' : 'Jelajahi anggota dan ketuk hati untuk menyukai'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {profiles.map((p, i) => {
              const photo = getPhoto(p);
              const city = p.city || p.domicile?.split(' - ').pop() || '';
              const genderLbl = p.gender === 'male' ? (lang === 'en' ? 'M' : 'L') : p.gender === 'female' ? (lang === 'en' ? 'F' : 'P') : '';
              return (
                <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  onClick={() => { setSelectedProfile(p); setPhotoIdx(0); }} data-testid={`liked-card-${p.id}`}
                  className="glass-card rounded-2xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform relative flex flex-col">
                  <div className="aspect-[3/4] bg-[var(--bg-input)] relative">
                    {photo ? <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover object-center" loading="lazy" /> : <div className="absolute inset-0 w-full h-full flex items-center justify-center"><User className="w-12 h-12 t-text-muted" /></div>}
                    {p.is_matched && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                        <div className="bg-red-600/90 text-white font-black text-xs px-4 py-1 -rotate-[30deg] shadow-lg tracking-widest">MATCHED</div>
                      </div>
                    )}
                    <div className="absolute top-1.5 left-1.5 p-1 rounded-full bg-[#D98A94] shadow">
                      <Heart className="w-3 h-3 text-white fill-white" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-2">
                      <p className="text-white font-bold text-[13px] leading-tight truncate">{p.name || (lang === 'en' ? 'Unnamed' : 'Tanpa Nama')}</p>
                      <p className="text-white/85 text-[11px] leading-tight truncate">{p.age}{genderLbl ? ` ${genderLbl}` : ''}{city ? ` · ${city}` : ''}</p>
                    </div>
                  </div>
                  <div className="p-2 space-y-1">
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] font-medium bg-[#D98A94]/12 text-[#D98A94] px-1.5 py-0.5 rounded-full">{p.category}</span>
                      {p.mbti && <span className="text-[9px] font-medium bg-[#C7867A]/12 text-[#C7867A] px-1.5 py-0.5 rounded-full">{p.mbti}</span>}
                      {p.marriage_status && <span className="text-[9px] font-medium bg-purple-500/10 text-purple-500 px-1.5 py-0.5 rounded-full">{getMarriageLabel(p.marriage_status)}</span>}
                    </div>
                    {p.occupation && <div className="flex items-center gap-1 t-text-muted text-[10px] truncate"><Briefcase className="w-2.5 h-2.5 shrink-0" /><span className="truncate">{p.occupation}</span></div>}
                    {p.education && <div className="flex items-center gap-1 t-text-muted text-[10px] truncate"><span className="shrink-0">🎓</span><span className="truncate">{p.education}</span></div>}
                    {p.height_cm && <div className="t-text-muted text-[10px]">📏 {p.height_cm} cm</div>}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Simple detail modal */}
      <Dialog open={!!selectedProfile} onOpenChange={(o) => { if (!o) closeProfileModal(); }}>
        <DialogContent className="glass-card t-text max-w-lg max-h-[85vh] overflow-y-auto p-0 [&>button.absolute]:hidden">
          {selectedProfile && (() => {
            const validPhotos = selectedProfile.photos?.filter(p => p) || [];
            return (
              <motion.div
                drag="y"
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.6 }}
                onDragEnd={(_, info) => {
                  if (info.offset.y > 120 || info.velocity.y > 600) closeProfileModal();
                }}
                className="relative"
              >
                {/* Big close button — pill with icon + text for maximum visibility.
                    Wrapped in div so the parent [&>button.absolute]:hidden override doesn't hide this button. */}
                <div className="absolute top-3 right-3 z-40">
                  <button
                    onClick={closeProfileModal}
                    data-testid="liked-modal-close-btn"
                    aria-label={lang === 'en' ? 'Close' : 'Tutup'}
                    className="flex items-center gap-1.5 pl-3 pr-4 py-2.5 rounded-full bg-white text-[#D98A94] shadow-2xl border-2 border-[#D98A94] hover:bg-[#D98A94] hover:text-white active:scale-95 transition font-bold text-sm"
                  >
                    <X className="w-5 h-5" strokeWidth={3} />
                    <span>{lang === 'en' ? 'Close' : 'Tutup'}</span>
                  </button>
                </div>
                {/* Photo area — uses object-contain so portraits are NEVER cropped.
                    Drag-to-close gesture is now activated ONLY from the small pill handle at the top
                    (a 80x24 px hit zone). The rest of the photo area allows normal vertical scroll. */}
                <div className="relative w-full bg-black flex items-center justify-center" style={{ minHeight: '280px', maxHeight: '55vh' }}>
                  {/* Drag-to-close handle (the only element with touchAction:none) */}
                  <div
                    onPointerDown={(e) => dragControls.start(e)}
                    style={{ touchAction: 'none' }}
                    data-testid="liked-modal-drag-handle"
                    aria-label={lang === 'en' ? 'Drag down to close' : 'Geser ke bawah untuk tutup'}
                    className="absolute top-0 left-1/2 -translate-x-1/2 z-30 w-20 h-7 flex items-center justify-center cursor-grab active:cursor-grabbing"
                  >
                    <div className="w-10 h-1.5 rounded-full bg-white/80 shadow" />
                  </div>
                  {validPhotos.length > 0 ? (
                    <>
                      <img
                        src={validPhotos[photoIdx] || validPhotos[0]}
                        alt=""
                        className="w-full h-auto object-contain pointer-events-none select-none"
                        draggable={false}
                        style={{ maxHeight: '55vh' }}
                      />
                      {validPhotos.length > 1 && (
                        <>
                          <div className="absolute top-9 left-0 right-0 flex justify-center gap-1.5 pointer-events-none z-10">
                            {validPhotos.map((photo, i) => <button key={`${photo.slice(-20)}-${i}`} onClick={(e) => { e.stopPropagation(); setPhotoIdx(i); }} className={`h-1 rounded-full transition-all pointer-events-auto ${i === photoIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/40'}`} />)}
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => i > 0 ? i - 1 : validPhotos.length - 1); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 text-white flex items-center justify-center z-10">&lsaquo;</button>
                          <button onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => i < validPhotos.length - 1 ? i + 1 : 0); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 text-white flex items-center justify-center z-10">&rsaquo;</button>
                        </>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5 pointer-events-none">
                        <h2 className="text-white text-xl font-bold drop-shadow">{selectedProfile.name || 'Member'}, {selectedProfile.age}</h2>
                        <p className="text-white/80 text-sm drop-shadow">{selectedProfile.domicile}</p>
                      </div>
                      {selectedProfile.is_matched && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                          <div className="bg-red-600/90 text-white font-black text-2xl px-10 py-3 -rotate-[25deg] shadow-xl tracking-widest border-4 border-white/30">MATCHED</div>
                        </div>
                      )}
                    </>
                  ) : <div className="w-full h-72 flex items-center justify-center"><User className="w-20 h-20 t-text-muted" /></div>}
                </div>

                {/* Scroll hint — bouncing pill telling users to scroll for WhatsApp/IG buttons */}
                <div className="relative">
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 z-20 pointer-events-none flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D98A94] text-white text-[11px] font-bold shadow-lg animate-bounce">
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={3} />
                    <span>{lang === 'en' ? 'Scroll down for contact' : 'Geser ke bawah untuk kontak'}</span>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs bg-[#D98A94]/10 text-[#D98A94] px-3 py-1 rounded-full">{selectedProfile.category}</span>
                    {selectedProfile.mbti && <span className="text-xs bg-[#C7867A]/10 text-[#C7867A] px-3 py-1 rounded-full">{selectedProfile.mbti}</span>}
                    {selectedProfile.marriage_status && <span className="text-xs bg-purple-500/10 text-purple-400 px-3 py-1 rounded-full">{getMarriageLabel(selectedProfile.marriage_status)}</span>}
                  </div>
                  {selectedProfile.bio && <p className="t-text-secondary text-sm">{selectedProfile.bio}</p>}
                  {selectedProfile.occupation && <div className="flex items-center gap-2 t-text-muted text-sm"><Briefcase className="w-4 h-4" />{selectedProfile.occupation}</div>}
                  {selectedProfile.domicile && <div className="flex items-center gap-2 t-text-muted text-sm"><MapPin className="w-4 h-4" />{selectedProfile.domicile}</div>}

                  {selectedProfile.is_matched ? (
                    <div className="glass-card rounded-xl p-4 text-center">
                      <CheckCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
                      <p className="t-text font-semibold text-sm">{lang === 'en' ? 'This person has been matched!' : 'Orang ini telah berjodoh!'}</p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedProfile.whatsapp && <a href={`https://wa.me/${selectedProfile.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-500/10 text-green-500 text-sm font-medium"><Phone className="w-4 h-4" />WhatsApp</a>}
                      {selectedProfile.instagram && <a href={`https://instagram.com/${selectedProfile.instagram}`} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 rounded-xl bg-purple-500/10 text-purple-500 text-sm font-medium"><ExternalLink className="w-4 h-4" />Instagram</a>}
                      {selectedProfile.tiktok && <a href={`https://tiktok.com/@${selectedProfile.tiktok}`} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 rounded-xl bg-black/10 t-text text-sm font-medium"><ExternalLink className="w-4 h-4" />TikTok</a>}
                      {selectedProfile.facebook && <a href={selectedProfile.facebook.startsWith('http') ? selectedProfile.facebook : `https://facebook.com/${selectedProfile.facebook}`} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-600/10 text-blue-500 text-sm font-medium"><ExternalLink className="w-4 h-4" />Facebook</a>}
                    </div>
                  )}

                  {/* Report button */}
                  <ReportButton profile={selectedProfile} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs t-text-muted hover:text-red-500 hover:bg-red-500/5 transition" />
                </div>
              </motion.div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <BottomNav active="liked" />
    </div>
  );
}
